import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { BaseContext } from "../../../../contexts/BaseContext";
import { getStatusClass, getStatusIcon } from "../utils";
import { deasyWorkflowService } from "../../../../services/api";
import { FiLoader, FiBarChart2 } from "react-icons/fi";

export const BaseJobCard = ({
  job,
  onJobChange,
  title,
  icon,
  fields,
  fieldValues,
  setFieldValues,
  children,
}) => {
  const { deasyApiKey } = useContext(BaseContext);
  const { status: initialStatus = "pending", progress_tracker_id } = job || {};

  // Store all job and state data in refs to avoid dependency cycles
  const jobRef = useRef(job);
  const progressTrackerIdRef = useRef(progress_tracker_id);
  const apiKeyRef = useRef(deasyApiKey);
  const statusRef = useRef(initialStatus);
  const fieldValuesRef = useRef(fieldValues);

  // Update refs when props change
  useEffect(() => {
    jobRef.current = job;
    progressTrackerIdRef.current = progress_tracker_id;
    apiKeyRef.current = deasyApiKey;
    statusRef.current = initialStatus;
    fieldValuesRef.current = fieldValues;
  }, [job, progress_tracker_id, deasyApiKey, initialStatus, fieldValues]);

  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(initialStatus);
  const [loading, setLoading] = useState(false);

  // Update status ref when state changes
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Update parent when field values change
  const updateJob = useCallback(() => {
    if (!onJobChange) return;

    const currentJob = jobRef.current;
    const currentFieldValues = fieldValuesRef.current;

    // Only copy essential properties to avoid circular references
    const updatedRequestBody = { ...currentJob.endpoint_request_body };

    Object.keys(currentFieldValues).forEach((key) => {
      updatedRequestBody[key] = currentFieldValues[key];
    });

    // Create a clean job object with only the properties we need, avoiding circular references
    const updatedJob = {
      id: currentJob.id,
      endpoint: currentJob.endpoint,
      status: currentJob.status,
      progress_tracker_id: currentJob.progress_tracker_id,
      endpoint_request_body: updatedRequestBody,
      // Include any other essential properties but avoid circular structures
    };

    onJobChange(updatedJob);
  }, [onJobChange]);

  // Only call updateJob when fieldValues change
  useEffect(
    () => {
      updateJob();
    },
    // eslint-disable-next-line
    [fieldValues],
  );

  // Set up progress tracking only once when component mounts
  useEffect(() => {
    const shouldTrackProgress = () => {
      const trackerId = progressTrackerIdRef.current;
      const currentStatus = statusRef.current;
      return (
        trackerId &&
        (currentStatus === "in_progress" || currentStatus === "pending")
      );
    };

    if (!shouldTrackProgress()) {
      return;
    }

    let isMounted = true;
    let intervalId = null;

    const fetchProgress = async () => {
      if (!isMounted || !shouldTrackProgress()) return;

      try {
        const trackerId = progressTrackerIdRef.current;
        const apiKey = apiKeyRef.current;

        const response = await deasyWorkflowService.getProgressTracker(
          apiKey,
          trackerId,
        );

        if (!isMounted) return;

        if (response?.data) {
          setProgress(response.data.completion_percentage || 0);

          const newStatus = response.data.status || statusRef.current;
          setStatus(newStatus);

          if (newStatus === "completed" || newStatus === "failed") {
            setLoading(false);
            clearInterval(intervalId);
          } else {
            setLoading(true);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          console.warn("Error fetching progress:", error);
          setLoading(false);
        }
      }
    };

    // Initial fetch
    fetchProgress();

    // Set up polling every 3 seconds
    intervalId = setInterval(fetchProgress, 3000);

    // Clean up on unmount
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []); // Empty dependency array - only run on mount

  const isDisabled = status === "in_progress";

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 hover:border-primary/40 hover:shadow-lg transition-all duration-300 overflow-hidden relative min-h-[330px] flex flex-col">
      <div className="flex justify-between items-center mb-5 relative">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            {icon}
          </div>
          <h3 className="text-xl font-bold text-gray-800 group-hover:text-primary transition-colors duration-300">
            {title}
          </h3>
        </div>
        <div
          className={`px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide ${getStatusClass(
            status,
          )} flex items-center shadow-sm`}
        >
          {getStatusIcon(status)}
          {status.replace("_", " ").toUpperCase()}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
        {fields.map((field) => (
          <div
            key={field.id}
            className="group/field transition-all duration-200"
          >
            <label
              className="block text-sm font-medium text-gray-600 mb-2 group-hover/field:text-primary transition-colors duration-200 flex items-center"
              htmlFor={`${field.id}-${job?.id}`}
            >
              {field.icon && (
                <span className="mr-1.5 text-primary/70">{field.icon}</span>
              )}
              {field.label}
            </label>
            {field.renderField({
              id: `${field.id}-${job?.id}`,
              value: fieldValues[field.id],
              onChange: (value) => {
                setFieldValues({
                  ...fieldValues,
                  [field.id]: value,
                });
              },
              isDisabled,
              job,
            })}
          </div>
        ))}
      </div>

      {children}

      {(progress !== null || loading) && progress_tracker_id && (
        <div className="mt-6">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 flex items-center">
              <FiBarChart2 className="mr-1.5 text-primary" />
              Progress
            </span>
            <span className="text-sm font-medium text-gray-700 tabular-nums">
              {loading ? "Calculating..." : `${progress}%`}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden shadow-inner">
            <div
              className={`h-3 rounded-full transition-all duration-700 ease-out ${
                status === "failed"
                  ? "bg-red-500"
                  : "bg-primary bg-gradient-to-r from-primary/80 to-primary"
              }`}
              style={{ width: `${loading ? 5 : progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          {loading && (
            <div className="text-sm text-gray-500 mt-2.5 flex items-center justify-start">
              <FiLoader className="animate-spin mr-2 h-4 w-4 text-primary" />
              Processing job...
            </div>
          )}
        </div>
      )}
    </div>
  );
};
